import * as THREE from 'three';
import { MAX_CAST_TRIANGLES, modelHit, triangleCount } from './picking';

// A floor hit this many scene radii beyond the part is the horizon, and would
// make every zoom step enormous.
const FLOOR_REACH = 20;

/**
 * How deep a wheel step should zoom towards what is under the cursor. Zooming
 * to the cursor moves by a share of the distance to the orbit target, so a
 * target left floating by a pan or a long zoom out stalls short of the part.
 */
export class ZoomPivot {
  private readonly raycaster = new THREE.Raycaster();
  private readonly floor = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
  private readonly box = new THREE.Box3();
  private readonly center = new THREE.Vector3();
  private readonly forward = new THREE.Vector3();
  private readonly candidate = new THREE.Vector3();
  private readonly nearest = new THREE.Vector3();
  private radius = 1;
  private model: THREE.Object3D | null = null;
  private exact = false;
  private clip: THREE.Plane | null = null;

  setScene(model: THREE.Object3D | null, box: THREE.Box3, floor: number): void {
    this.model = model;
    // Past the limit the bounding box is close enough.
    this.exact = model !== null && triangleCount(model) <= MAX_CAST_TRIANGLES;
    this.box.copy(box);
    const empty = box.isEmpty();
    if (empty) this.center.set(0, 0, floor);
    else box.getCenter(this.center);
    this.radius = empty ? 1 : Math.max(box.getSize(new THREE.Vector3()).length() / 2, 1e-3);
    this.floor.constant = -floor;
  }

  setClip(plane: THREE.Plane | null): void {
    this.clip = plane;
  }

  /** Distance along the view axis to the surface under `pointer`, if there is one. */
  depthAt(camera: THREE.Camera, pointer: THREE.Vector2): number | null {
    this.raycaster.setFromCamera(pointer, camera);
    const hit = this.partHit() ?? this.floorHit(camera);
    if (!hit) return null;
    camera.getWorldDirection(this.forward);
    const depth = hit.sub(camera.position).dot(this.forward);
    return depth > 0 ? depth : null;
  }

  private partHit(): THREE.Vector3 | null {
    if (!this.model) return null;
    const { ray } = this.raycaster;
    let best = Infinity;
    const consider = (point: THREE.Vector3 | null) => {
      if (!point) return;
      const distance = point.distanceTo(ray.origin);
      if (distance >= best) return;
      best = distance;
      this.nearest.copy(point);
    };
    if (this.exact) consider(modelHit(this.raycaster, this.model, this.clip)?.point ?? null);
    else consider(ray.intersectBox(this.box, this.candidate));
    // The cut face of a section is the cap, which is not part of the model.
    if (this.clip && ray.intersectPlane(this.clip, this.candidate) && this.box.containsPoint(this.candidate)) {
      consider(this.candidate);
    }
    return best < Infinity ? this.nearest : null;
  }

  private floorHit(camera: THREE.Camera): THREE.Vector3 | null {
    const point = this.raycaster.ray.intersectPlane(this.floor, this.candidate);
    if (!point) return null;
    const reach = camera.position.distanceTo(this.center) + this.radius * FLOOR_REACH;
    return point.distanceTo(camera.position) <= reach ? point : null;
  }
}
